// Internal, flag-gated WonderLabs mission launcher. It does not own Journey
// progress: AITutorApp loads the existing saved state when a tile is opened.
import React from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { JOURNEY_ASSET_BASE_URL } from "../../../config";

// Shared learner-facing mission presentation for both WonderLabs adventure tiles
// and Step 0 Mission Home. Keep hooks short; Swan expands the invitation in Step 0.
export const WONDERLABS_MISSIONS = [
  {
    slug: "flood-rescue-bridge",
    posterPath: "flood-rescue-bridge/prelude/flood_rescue_bridge_story_poster.jpg",
    title: "Flood Rescue Bridge",
    eyebrow: "BRIDGE ENGINEERING",
    story: "Oh no! The bridge is broken. Can you build a stronger one?",
    swan: "We have a big problem, engineer! People need to get across the river. Do you think we could build a strong bridge using paper? Let's try!",
    invitation: "Design · Build · Rescue",
    symbol: "🌉",
    accent: "#0284c7",
    tint: "#e0f2fe",
  },
  {
    slug: "electric-spiral-mission",
    posterPath: "electric-spiral/prelude/breeze_station_story_poster.jpg",
    title: "Breeze Station",
    eyebrow: "ELECTRICITY & MOTION",
    story: "The station has gone silent. Can you get it working again?",
    swan: "Something's wrong at Breeze Station! The beacon is dark, and the Wind Engine has stopped. Two mysteries to solve—and I need a curious engineer like you!",
    invitation: "Investigate · Build · Restore",
    symbol: "⚡",
    accent: "#b45309",
    tint: "#fef3c7",
  },
  {
    slug: "conductive-dough-mission",
    posterPath: "conductive-dough/prelude/glow_city_story_poster.jpg",
    title: "Glow City",
    eyebrow: "CIRCUIT DETECTIVE",
    story: "Can you bring Glow City back to life?",
    swan: "The city needs your help, detective! Could your experiments help its lights shine again?",
    invitation: "Investigate · Connect · Illuminate",
    symbol: "💡",
    accent: "#7c3aed",
    tint: "#f3e8ff",
  },
  {
    slug: "wind-powered-car-mission",
    posterPath: "wind-powered-car/prelude/green_valley_story_poster.jpg",
    title: "Mystery Rover",
    eyebrow: "FORCES & DESIGN",
    story: "Nobody is pushing this rover. So what's making it move?",
    swan: "Something mysterious is happening! This rover can move without anyone pushing it. Think you can uncover its secret? We'll need some clever experiments!",
    invitation: "Observe · Experiment · Uncover",
    symbol: "🚙",
    accent: "#047857",
    tint: "#dcfce7",
  },
  {
    slug: "robot-artist-mission",
    posterPath: "drawing-robot/prelude/robot_artist_story_poster.jpg",
    title: "Robot Artist",
    eyebrow: "ART & ENGINEERING",
    story: "These strange drawings are clues. Can you discover who—or what—made them?",
    swan: "Look at those wild loops and swirls! Something made these drawings, but what? You're the detective. Let's examine the evidence before we make a guess!",
    invitation: "Observe · Imagine · Investigate",
    symbol: "🎨",
    accent: "#c2410c",
    tint: "#ffedd5",
  },
  {
    slug: "food-delivery-robot-mission",
    posterPath: "food-delivery-robot/prelude/story_poster.jpg",
    title: "The Wobbly Lunch Express",
    eyebrow: "MOTION & ENGINEERING · AGES 6–7",
    story: "Can a tiny moving helper deliver its lunch without a tumble?",
    swan: "A tiny lunch needs delivering! Let’s predict, build, test, and see what we can discover.",
    invitation: "Predict · Build · Improve",
    symbol: "🤖",
    accent: "#0f766e",
    tint: "#ccfbf1",
  },
  {
    slug: "magnetworks-mission",
    posterPath: "magnetworks/prelude/story_poster.jpg",
    title: "The Invisible Rescue",
    eyebrow: "INVISIBLE FORCES · AGES 5–7",
    story: "Can you help Pip travel home using an invisible push or pull?",
    swan: "Pip needs a rescue! Can you discover how to move a little car without touching it? Let's investigate your own evidence.",
    invitation: "Predict · Rescue · Discover",
    symbol: "🧲",
    accent: "#0f766e",
    tint: "#ccfbf1",
  },
  {
    slug: "robot-artist-explorers-mission",
    posterPath: "drawing-robot/prelude/robot_artist_story_poster.jpg",
    title: "Robot Artist",
    eyebrow: "ART & ENGINEERING · AGES 6–7",
    story: "Four mysterious drawings appeared overnight. Can you discover the artist and make your own?",
    swan: "A strange picture appeared in the art room! Can you spot a clue? We'll discover, build and experiment together.",
    invitation: "Notice · Build · Discover",
    symbol: "🎨",
    accent: "#c2410c",
    tint: "#ffedd5",
  },
];

// Explicit catalog membership and order. Do not duplicate Journey data or IDs.
// Keep the combined catalog available for legacy ?ff_swan_unified_panel=1 URLs.
export const WONDERLABS_CATALOG_SLUGS = Object.freeze({
  inventors: [
    "flood-rescue-bridge",
    "electric-spiral-mission",
    "conductive-dough-mission",
    "wind-powered-car-mission",
    "robot-artist-mission",
  ],
  explorers: [
    "magnetworks-mission",
    "food-delivery-robot-mission",
    "robot-artist-explorers-mission",
  ],
});

export const getWonderLabsMissionsForCatalog = (catalog = "all") => {
  const slugs = WONDERLABS_CATALOG_SLUGS[catalog];
  if (!slugs) return WONDERLABS_MISSIONS;
  return slugs.map((slug) => WONDERLABS_MISSIONS.find((mission) => mission.slug === slug))
    .filter(Boolean);
};

// Use the exact Prelude poster URLs already used by JourneyStoryPrelude.
// The media stays on the existing backend/static asset endpoint.
export const getWonderLabsPosterUrl = (posterPath) => {
  const cleanPath = String(posterPath || "").replace(/^\/+/, "");
  return cleanPath ? `${JOURNEY_ASSET_BASE_URL}/${cleanPath}` : "";
};

function WonderLabsMissionCard({ mission, journeyId, disabled, loading, onOpenMission, surface, foreground, muted, isDarkMode, width }) {
  const [posterFailed, setPosterFailed] = React.useState(false);
  const posterUrl = getWonderLabsPosterUrl(mission.posterPath);
  return (
    <Pressable
      onPress={() => onOpenMission?.(journeyId, mission.slug)}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={`Open ${mission.title} with Professor Swan`}
      accessibilityState={{ disabled }}
      style={({ pressed }) => [
        styles.card,
        { width, backgroundColor: surface, borderColor: isDarkMode ? "#334155" : "#d9e5ef" },
        pressed && !disabled ? { opacity: 0.8 } : null,
      ]}
    >
      <View style={[styles.posterFrame, { backgroundColor: mission.tint }]}>
        {posterUrl && !posterFailed ? (
          <Image
            source={{ uri: posterUrl }}
            resizeMode="cover"
            accessibilityLabel={`${mission.title} opening story artwork`}
            onError={() => setPosterFailed(true)}
            style={styles.posterImage}
          />
        ) : (
          <Text style={styles.posterFallback}>{mission.symbol}</Text>
        )}
      </View>
      <View style={styles.cardContent}>
        <Text style={[styles.eyebrow, { color: mission.accent }]}>{mission.eyebrow}</Text>
        <Text style={[styles.cardTitle, { color: foreground }]}>{mission.title}</Text>
        <Text style={[styles.story, { color: muted }]}>{mission.story}</Text>
        <View style={styles.cardBottom}>
          <Text style={[styles.action, { color: disabled ? muted : mission.accent }]}>
            {loading ? "Loading missions…" : journeyId ? "Open adventure  →" : "Not available yet"}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

export const getWonderLabsJourneyId = (entry) => {
  const candidate = entry?.journey?.id ?? entry?.journey ?? entry?.journey_id ?? entry?.journeyId ?? entry?.id ?? entry;
  const id = Number(candidate);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export default function WonderLabsMissionHome({
  journeysBySlug = {},
  loading = false,
  opening = false,
  onOpenMission,
  onOpenRegularHome,
  isDarkMode = false,
  catalog = "all",
}) {
  const { width } = useWindowDimensions();
  const missions = getWonderLabsMissionsForCatalog(catalog);
  const catalogTitle = catalog === "inventors" ? "Inventors · Ages 8–12"
    : catalog === "explorers" ? "Explorers · Ages 5–7"
      : "Choose your next science adventure";
  const columns = width >= 1220 ? 3 : width >= 760 ? 2 : 1;
  const cardWidth = columns === 3 ? "31.8%" : columns === 2 ? "48.6%" : "100%";
  const foreground = isDarkMode ? "#f8fafc" : "#10253f";
  const muted = isDarkMode ? "#cbd5e1" : "#475569";
  const surface = isDarkMode ? "#172b44" : "#ffffff";

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: isDarkMode ? "#0f172a" : "#f3f8fc" }}
      contentContainerStyle={styles.scrollContent}
      accessibilityLabel="WonderLabs science adventures"
    >
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <Image source={require("../../../../assets/Saras-3D_Logo.png")} resizeMode="contain"
            accessibilityLabel="Saras-3D logo" style={{ width: 92, height: 50, marginRight: 10 }} />
          <View style={styles.headingText}>
            <Text style={[styles.brand, { color: isDarkMode ? "#7dd3fc" : "#0369a1" }]}>WONDERLABS</Text>
            <Text style={[styles.title, { color: foreground }]}>{catalogTitle}</Text>
            <Text style={[styles.subtitle, { color: muted }]}>Explore, experiment, and solve a mystery with Professor Swan.</Text>
          </View>
        </View>

        <View style={[styles.hintRow, { backgroundColor: isDarkMode ? "#1e3a5a" : "#e8f4ff" }]}>
          <Text style={[styles.hintText, { color: foreground }]}>🦢  Pick a mission. Professor Swan will guide you from where you left off.</Text>
        </View>

        <View style={styles.cards}>
          {missions.map((mission) => {
            const entry = journeysBySlug instanceof Map
              ? journeysBySlug.get(mission.slug)
              : journeysBySlug?.[mission.slug];
            const journeyId = getWonderLabsJourneyId(entry);
            const disabled = loading || opening || journeyId == null;
            return (
              <WonderLabsMissionCard
                key={mission.slug}
                mission={mission}
                journeyId={journeyId}
                disabled={disabled}
                loading={loading}
                onOpenMission={onOpenMission}
                surface={surface}
                foreground={foreground}
                muted={muted}
                isDarkMode={isDarkMode}
                width={cardWidth}
              />
            );
          })}
        </View>
        {loading || opening ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#0284c7" />
            <Text style={[styles.loadingText, { color: muted }]}>{opening ? "Opening your adventure…" : "Finding your adventures…"}</Text>
          </View>
        ) : null}
        <Text style={[styles.footer, { color: muted }]}>Your adventure progress is saved as you go.</Text>
        {onOpenRegularHome ? (
          <Pressable
            onPress={onOpenRegularHome}
            accessibilityRole="button"
            accessibilityLabel="Open regular Saras-3D home (internal preview)"
            style={styles.internalLink}
          >
            <Text style={[styles.internalLinkText, { color: muted }]}>Internal preview · Open regular Saras-3D home</Text>
          </Pressable>
        ) : null}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollContent: { flexGrow: 1, paddingHorizontal: 22, paddingVertical: 26 },
  container: { width: "100%", maxWidth: 1180, alignSelf: "center" },
  headerRow: { flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18, flexWrap: "wrap" },
  headingText: { flexGrow: 1, flexShrink: 1, maxWidth: 850 },
  brand: { fontSize: 12, fontWeight: "900", letterSpacing: 2.2, marginBottom: 8 },
  title: { fontSize: 29, lineHeight: 36, fontWeight: "900", marginBottom: 6 },
  subtitle: { fontSize: 15, lineHeight: 23 },
  hintRow: { borderRadius: 12, paddingHorizontal: 17, paddingVertical: 13, marginBottom: 20 },
  hintText: { fontSize: 14, lineHeight: 21, fontWeight: "600" },
  cards: { flexDirection: "row", flexWrap: "wrap", gap: 14 },
  card: { minHeight: 322, borderWidth: 1, borderRadius: 18, overflow: "hidden" },
  posterFrame: { height: 168, width: "100%", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  posterImage: { width: "100%", height: "100%" },
  posterFallback: { fontSize: 48 },
  cardContent: { flex: 1, padding: 18 },
  eyebrow: { fontSize: 11, fontWeight: "800", letterSpacing: 0.7, marginBottom: 5 },
  cardTitle: { fontSize: 21, fontWeight: "800", marginBottom: 6 },
  story: { fontSize: 14, lineHeight: 20 },
  cardBottom: { marginTop: "auto", paddingTop: 15 },
  action: { fontSize: 14, fontWeight: "800" },
  loadingRow: { flexDirection: "row", alignItems: "center", gap: 9, marginTop: 16 },
  loadingText: { fontSize: 13 },
  footer: { fontSize: 12, marginTop: 24, textAlign: "center" },
  internalLink: { alignSelf: "center", marginTop: 8, paddingHorizontal: 14, paddingVertical: 9 },
  internalLinkText: { fontSize: 11, textDecorationLine: "underline" },
});
